import React, { useState } from 'react';
import { FixedAsset, FixedAssetsService, DepreciationScheduleLine } from '@repo/entity-accounting';

export function FixedAssetsRoute() {
  const [assets, setAssets] = useState<FixedAsset[]>([
    {
      id: 'fa-1',
      tenantId: 'tenant-1',
      name: 'Company Car',
      assetAccountId: '1500',
      accumulatedDepreciationAccountId: '1501',
      depreciationExpenseAccountId: '6000',
      purchaseDate: '2025-01-01T00:00:00.000Z',
      purchasePrice: 1500000,
      salvageValue: 300000,
      usefulLifeMonths: 60,
      depreciationMethod: 'Straight-Line',
      status: 'Active',
    }
  ]);
  
  const [selectedAsset, setSelectedAsset] = useState<FixedAsset | null>(null);
  const [schedule, setSchedule] = useState<DepreciationScheduleLine[]>([]);
  const service = new FixedAssetsService();

  const handleViewSchedule = (asset: FixedAsset) => {
    setSelectedAsset(asset);
    const sched = asset.depreciationMethod === 'Straight-Line'
      ? service.calculateStraightLineDepreciation(asset)
      : service.calculateDecliningBalance(asset, 2);
    setSchedule(sched);
  };

  const formatPHP = (amount: number) => {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount);
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4 text-gray-900">Fixed Asset Register</h1>
      <p className="text-gray-600 mb-6">Manage fixed assets and view depreciation schedules (₱ PHP).</p>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-6 py-4 text-sm font-semibold text-gray-900">Asset Name</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-900">Purchase Date</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-900">Purchase Price</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-900">Method</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-900">Status</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-900">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {assets.map(asset => (
              <tr key={asset.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{asset.name}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{new Date(asset.purchaseDate).toLocaleDateString()}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{formatPHP(asset.purchasePrice)}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{asset.depreciationMethod}</td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${asset.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {asset.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm">
                  <button 
                    onClick={() => handleViewSchedule(asset)}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View Schedule
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedAsset && (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">
            Depreciation Schedule: {selectedAsset.name}
          </h2>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-100 sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-sm font-semibold text-gray-900">Month</th>
                  <th className="px-6 py-3 text-sm font-semibold text-gray-900">Depreciation Expense</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {schedule.map((line, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-600">
                      {new Date(line.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600">
                      {formatPHP(line.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
